import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import get from 'lodash/get';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Score, { SCORE_TYPE } from '../atoms/Score';
import Checkbox from '../atoms/Checkbox';
import TableHeadCell from '../atoms/TableHeadCell';
import Input from '../atoms/Input';
import EditButton from '../atoms/EditButton';
import CancelButton from '../atoms/CancelButton';
import DoneButton from '../atoms/DoneButton';
import Loader from '../atoms/Loader';
import Tooltip from '../atoms/Tooltip';
import ColorCode from '../atoms/ColorCode';
import DragNDropDots from '../atoms/DragNDrop';
import SelectPlayer from '../molecules/SelectPlayer';
import {
  COLOR_BACKGROUND_LIGHT,
  COLOR_BLACK,
  COLOR_BLUE,
  COLOR_DARK_BLUE,
  COLOR_DARK_GRAY,
  COLOR_GREEN,
  COLOR_LIGHT_GRAY, COLOR_MIDDLE_GRAY, COLOR_ORANGE,
  COLOR_SHADOW,
  COLOR_TEXT,
  COLOR_WHITE,
} from '../styles/colors';
import MEDIA from '../styles/media';
import DepthChartPosition from '../types/DepthChartPosition';
import DepthChartPlayer from '../types/DepthChartPlayer';
import Player from '../types/Player';
import { Color } from '../types/Color';
import Avatar from '../atoms/Avatar';
import MultiSportList from '../molecules/MultiSportList';
import CircleBadge from '../atoms/CircleBadge';
import { Typography } from '@material-ui/core';
import NotAvailable from '../atoms/NotAvailable';

interface DepthChartProps {
  className?: string;
  depthChartPositions: DepthChartPosition[];
  selectedPlayers: DepthChartPlayer[];
  setSelectedPlayers: (players:DepthChartPlayer[]) => void;
  editedPlayers: DepthChartPlayer[];
  setEditedPlayers: (players:DepthChartPlayer[]) => void;
  teamPlayers: Player[];
  fetchPlayerDetails: (playerId:number) => Promise<Player | void>;
  createDepthChartPlayerFromPlayer: (depthChartPlayer:DepthChartPlayer) => (player:Player | void) => Promise<DepthChartPlayer>;
  printed?: boolean;
  onUpdatePositions: (positions:DepthChartPosition[]) => void;
  onUpdatePlayers: (players:DepthChartPlayer[]) => void;
  onUpdateLabel: (position:DepthChartPosition, label:string) => void;
}

const DEPTH_CHART_COLUMN = {
  NUMBER: 'number',
  NAME: 'name',
  HEIGHT: 'height',
  WEIGHT: 'weight',
  CLASS: 'class',
  PAI_SCORE: 'pai',
  PPI_SCORE: 'ppi',
  COMBINE_SCORE: 'combine',
  PAI_PERCENTILE: 'pai-percentile',
  PPI_PERCENTILE: 'ppi-percentile',
  HS_SPORTS: 'multiSport',
  HS_SPEED_PERCENTILE: 'hsSpeedPercentile',
  HS_POWER_PERCENTILE: 'hsPowerPercentile',
  SEASON_LOG: "seasonLog",
  CAREER_LOG: "careerLog",
  POS_RANK: 'tfPosRank',
  TF_POS_RANK_PERCENTILE: 'tfPosPerc'
};

const DEFAULT_COLUMNS = [
  DEPTH_CHART_COLUMN.NUMBER,
  DEPTH_CHART_COLUMN.NAME,
  DEPTH_CHART_COLUMN.HEIGHT,
  DEPTH_CHART_COLUMN.WEIGHT,
  DEPTH_CHART_COLUMN.CLASS,
  DEPTH_CHART_COLUMN.HS_SPEED_PERCENTILE,
  DEPTH_CHART_COLUMN.HS_POWER_PERCENTILE,
  DEPTH_CHART_COLUMN.HS_SPORTS,
  DEPTH_CHART_COLUMN.PAI_SCORE,
  // DEPTH_CHART_COLUMN.PAI_PERCENTILE,
  DEPTH_CHART_COLUMN.COMBINE_SCORE,
  // DEPTH_CHART_COLUMN.COMBINE_PERCENTILE,
  DEPTH_CHART_COLUMN.PPI_SCORE,
  // DEPTH_CHART_COLUMN.PPI_PERCENTILE,
  DEPTH_CHART_COLUMN.POS_RANK,
  // DEPTH_CHART_COLUMN.TF_POS_RANK_PERCENTILE,
  DEPTH_CHART_COLUMN.SEASON_LOG,
  DEPTH_CHART_COLUMN.CAREER_LOG,
];

const DEPTH_CHART_COLUMN_TITLE = {
  [DEPTH_CHART_COLUMN.NUMBER]: 'No.',
  [DEPTH_CHART_COLUMN.NAME]: 'Name',
  [DEPTH_CHART_COLUMN.HEIGHT]: 'HT',
  [DEPTH_CHART_COLUMN.HS_SPORTS]: 
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
    <span>HS</span>
    <div style={{ width: "100%", height: "1px", backgroundColor: COLOR_DARK_GRAY, margin: "2px 0" }} />
    <span>Sports</span>
  </div>,
  [DEPTH_CHART_COLUMN.WEIGHT]: 'WT',
  [DEPTH_CHART_COLUMN.CLASS]: 'Class',
  // [DEPTH_CHART_COLUMN.PAI_SCORE]: 'PAI',
  [DEPTH_CHART_COLUMN.PAI_SCORE]:
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
    <span>PAI</span>
    <div style={{ width: "100%", height: "1px", backgroundColor: COLOR_DARK_GRAY, margin: "2px 0" }} />
    <span>Div&#8209;1<span style={{fontSize: '0.7rem'}}>%</span></span>
  </div>,
  // [DEPTH_CHART_COLUMN.COMBINE_SCORE]: 'Com.',
  [DEPTH_CHART_COLUMN.COMBINE_SCORE]: <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
    <span>Com.</span>
    <div style={{ width: "100%", height: "1px", backgroundColor: COLOR_DARK_GRAY, margin: "2px 0" }} />
    <span>Div&#8209;1<span style={{fontSize: '0.7rem'}}>%</span></span>
  </div>,
  // [DEPTH_CHART_COLUMN.PPI_SCORE]: 'PPI',
  [DEPTH_CHART_COLUMN.PPI_SCORE]: <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
    <span>PPI</span>
    <div style={{ width: "100%", height: "1px", backgroundColor: COLOR_DARK_GRAY, margin: "2px 0" }} />
    <span>Div&#8209;1<span style={{fontSize: '0.7rem'}}>%</span></span>
  </div>,
  // [DEPTH_CHART_COLUMN.PAI_PERCENTILE]: <>Div&#8209;1</>,
  [DEPTH_CHART_COLUMN.PPI_PERCENTILE]: <>Div&#8209;1<span style={{fontSize: '12px'}}><span style={{fontSize: '0.7rem'}}>%</span></span></>,
  // [DEPTH_CHART_COLUMN.HS_SPEED_PERCENTILE]: 'HS Speed',
  [DEPTH_CHART_COLUMN.HS_SPEED_PERCENTILE]: 
   <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
    <span>HS</span>
    <div style={{ width: "100%", height: "1px", backgroundColor: COLOR_DARK_GRAY, margin: "2px 0" }} />
    <span>Speed</span>
  </div>,
  [DEPTH_CHART_COLUMN.HS_POWER_PERCENTILE]: <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
    <span>HS</span>
    <div style={{ width: "100%", height: "1px", backgroundColor: COLOR_DARK_GRAY, margin: "2px 0" }} />
    <span>Power</span>
  </div>,
  [DEPTH_CHART_COLUMN.SEASON_LOG]: 'Season Log',
  [DEPTH_CHART_COLUMN.CAREER_LOG]: 'Career Log',
  // [DEPTH_CHART_COLUMN.POS_RANK]: 'Pos Rank',
  [DEPTH_CHART_COLUMN.POS_RANK]: 
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
    <span>Rank</span>
    <div style={{ width: "100%", height: "1px", backgroundColor: COLOR_DARK_GRAY, margin: "2px 0" }} />
    <span>Div&#8209;1
      <span style={{fontSize: '0.7rem'}}>%</span>
    </span>
  </div>,
  // [DEPTH_CHART_COLUMN.TF_POS_RANK_PERCENTILE]: <>Div&#8209;1</>,
};

const useStyles = makeStyles((theme: Theme) => ({
  depthChartWrap: {
    overflow: 'auto',
  },

  depthChart: {
    width: '100%',
    boxShadow: `0 10px 10px 0 ${COLOR_SHADOW}`,
    border: `1px solid ${COLOR_LIGHT_GRAY}`,
    background: COLOR_WHITE,
  },
  isDragging: {
    '& $tableRow': {
      background: `${COLOR_WHITE} !important`,
    },
  },

  tableHead: {
    borderBottom: `1px solid ${COLOR_LIGHT_GRAY}`,

    '& $checkBoxCell': {
      height: 'auto',
    },
  },

  tableRow: {
    background: COLOR_BACKGROUND_LIGHT,

    '&:nth-of-type(2n - 1)': {
      '& $playerLoaderSpinner': {
        marginTop: theme.spacing(0.5),
      },
    },
    '&:nth-of-type(2n)': {
      '& $playerLoaderSpinner': {
        marginTop: theme.spacing(-0.5),
      },
    },

    '&:nth-of-type(4n - 1)': {
      background: COLOR_WHITE,
    },
    '&:nth-of-type(4n)': {
      background: COLOR_WHITE,
    },

    '&:hover': {
      '& $dragNDropDots': {
        opacity: 1,
      },
    },
  },
  editedTableRow: {
    '& $dragNDropDots': {
      display: 'none',
    },
  },
  tableRowDragging: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },

  tableCell: {
    paddingLeft: theme.spacing(2.5),
    paddingRight: theme.spacing(2.5),
    paddingTop: theme.spacing(1.8),
    paddingBottom: theme.spacing(1.8),
    fontSize: theme.typography.pxToRem(14),
    border: 0,
    verticalAlign: 'top'
  },
  edited: {},

  positionCell: {
    width: '8%',
    minWidth: '110px',
    paddingLeft: '20px',
    lineHeight: 1,

    '&$edited': {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(1),
    },
  },

  checkBoxCell: {
    position: 'relative',
    width: '50px',
    minWidth: 0,
    height: '50px',
    // padding: '5px',
    paddingBottom: theme.spacing(1.8),
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
    paddingTop: theme.spacing(1.8),
    textAlign: 'center',
  },

  dndHeadCell: {
    width: '16px',
    padding: 0,
  },
  dndCell: {
    width: '16px',
    padding: 0,
  },
  dragNDropDots: {
    opacity: 0,
    transition: 'opacity 0.2s',
  },

  jerseyNumberHeadCell: {
    paddingRight: theme.spacing(0.5),
    paddingLeft: theme.spacing(0),
    textAlign: 'center',
    borderLeft: 0,
  },
  jerseyNumberCell: {
    width: '15px',
    maxWidth: '55px',
    // padding: theme.spacing(0),

    '&$edited': {
      width: '45px',
      paddingLeft: '2px',
      paddingRight: '22px',
    },
  },

  nameColumnHeadCell: {
    padding: theme.spacing(1.25),
  },

  nameCell: {
    minWidth: '145px',
    width: '145px',
    maxWidth: '145px',
    position: 'relative',
    padding: theme.spacing(1.25),
    '&$edited': {
      padding: theme.spacing(1, 0.5),
    },
  },

  playerNameWithLocationIcon: {
    display: 'flex',
    alignItems: 'center',
  },

  playerAvatar: {
    flexShrink: 0,
    width: '32px',
    height: '32px',
  },

  playerLink: {
    display: 'inline-block',
    maxWidth: 'ch',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: COLOR_BLUE,
    fontSize: theme.typography.pxToRem(16),
    paddingLeft: '8px',

    '&:hover': {
      color: COLOR_DARK_BLUE,
    }
  },

  nameWrap: {
    display: 'flex',
    flexDirection: 'column',
    '& span': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },

  duplicatePlayer: {
    marginRight: theme.spacing(1),
  },

  speedAndPowerRecuitDot: {
    marginLeft: theme.spacing(0.5),
  },

  playerLoader: {
    background: 'none',
  },
  playerLoaderSpinner : {
    width: '24px !important',
    height: '24px !important',
  },

  selectPlayer: {
    paddingRight: theme.spacing(4),
  },

  heightHeadCell: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    textAlign: 'center',
    width: '45px',

    '&:after': {
      content: '""',
      width: '1px',
      height: '12px',
      position: 'absolute',
      right: 0,
      background: COLOR_DARK_GRAY,
    },
  },
  heightCell: {
    width: '25px',
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),

    '&$edited': {
      paddingLeft: theme.spacing(0.5),
      paddingRight: theme.spacing(0.5),
    },
  },

  weightHeadCell: {
    paddingLeft: theme.spacing(1.2),
    paddingRight: theme.spacing(1.2),
    borderLeft: 0,

    '&:after': {
      content: '""',
      width: '1px',
      height: '12px',
      position: 'absolute',
      right: 0,
      background: COLOR_DARK_GRAY,
    },
  },
  weightCell: {
    width: '25px',
    paddingLeft: theme.spacing(1.2),
    paddingRight: theme.spacing(1.2),

    '&$edited': {
      paddingLeft: theme.spacing(0.5),
      paddingRight: theme.spacing(0.25),
    },
  },

  classHeadCell: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    borderLeft: 0,
  },
  classCell: {
    width: '25px',
    paddingLeft: theme.spacing(1.2),
    paddingRight: 0,

    '&$edited': {
      paddingLeft: theme.spacing(0.5),
      paddingRight: theme.spacing(0.5),
    },
  },

  playerScoreHeadCell: {
    width: '50px',
    padding: '10px 6px',
    textAlign: 'center',
  },
  playerScoreCell: {
    textAlign: 'center',
    paddingBottom: theme.spacing(1.8),
    paddingLeft: theme.spacing(0.75),
    paddingRight: theme.spacing(0.75),
    paddingTop: theme.spacing(1.8),
  },

  playerScore: {
    width: '32px',
    height: '32px',
    fontSize: theme.typography.pxToRem(14),
    margin: '0 auto',
  },

  playerScoreForPrint: {
    width: '30px',
    height: '30px',
    fontSize: theme.typography.pxToRem(12),
    margin: '0 auto',
  },

  paiPercentileHeadCell: {
    width: '34px',
    textAlign: 'center',
    padding: theme.spacing(0.75),
    borderLeft: 0,
  },
  paiPercentileCell: {
    height: '20px',
    // padding: theme.spacing(0),
    textAlign: 'center',
    fontWeight: 600,
    color: COLOR_ORANGE,
    fontSize: theme.typography.pxToRem(12),
    paddingLeft: theme.spacing(0.5),
  },

  combinePercentileHeadCell: {
    width: '46px',
    textAlign: 'center',
    padding: theme.spacing(0.75),
    borderLeft: 0,
  },
  combinePercentileCell: {
    height: '20px',
    // padding: theme.spacing(0),
    textAlign: 'center',
    fontWeight: 600,
    color: COLOR_GREEN,
    fontSize: theme.typography.pxToRem(12),
    paddingLeft: theme.spacing(0.5),
  },
  ppiPercentileHeadCell: {
    width: '14px',
    textAlign: 'center',
    padding: theme.spacing(0.75),
    borderLeft: 0,
  },
  ppiPercentileCell: {
    // padding: theme.spacing(0),
    textAlign: 'center',
    fontWeight: 600,
    color: COLOR_BLUE,
    fontSize: theme.typography.pxToRem(12),
    paddingLeft: theme.spacing(0.5),
  },
  multiSportHeadCell: {
    width: '44px',
    padding: theme.spacing(0, 0.5),
    textAlign: 'center',
  },
  multiSportCell: {
    paddingRight: 0,
    paddingLeft: 0,
    textAlign: 'center',
  },
  editHeadCell: {
    borderLeft: 0,
  },
  editCell: {
    paddingBottom: theme.spacing(1.8),
    paddingLeft: theme.spacing(0),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(1.8),
  },
  editButton: {
    width: '24px',
    height: '24px',

    '&:hover': {
      color: COLOR_BLUE,
    },
  },
  editButtonIcon: {
    width: '24px',
    height: '24px',
  },

  actions: {
    flexDirection: 'column',
    gap: '5px',
    // height: '32px',
    minWidth: '24px',
    display: 'flex',
    justifyContent: 'center',
  },

  input: {
    width: '100%',
    padding: 0,
  },
  inputField: {
    padding: theme.spacing(.5),
  },
  positionInputField: {
    width: '40px',
  },
  classInputField: {
    paddingLeft: theme.spacing(.5),
    paddingRight: theme.spacing(.5),
  },

  hidden: {
    display: 'none',
  },

  [MEDIA.DESKTOP]: {
    tableCell: {
      fontSize: theme.typography.pxToRem(16),
    },
  },

  multiSportIcon: {
    color: COLOR_WHITE
  },
  multiSportScoreCell: {
    margin: '0 auto',
  },
  posHeadCell: {
    fontWeight: 600,
    minWidth: '85px',
    maxWidth: '85px',
    width: '85px',
  },
  nameHeadCell: {
    width: '155px',
  },
  hsSpeedCell: {
    color: COLOR_ORANGE,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    fontWeight: 600,
    textAlign: 'center',
  },
  hsPowerCell: {
    color: COLOR_GREEN,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    fontWeight: 600,
    textAlign: 'center',
  },
  hsSpeedAndPowerHeadCell: {
    maxWidth: '52px',
    minWidth: '52px',
    padding: '10px 4px',
    textAlign: 'center',
    width: '52px',
  },
  tfPosRankHeadCell: {
    width: '55px',
    padding: '10px 8px',
    textAlign: 'center',
  },
  tfPosRankCell: {
    paddingBottom: theme.spacing(1.8),
    paddingLeft: theme.spacing(0.75),
    paddingRight: theme.spacing(0.75),
    paddingTop: theme.spacing(1.8),
  },
  posRankPercentileHeadCell: {
    borderLeft: 0,
    padding: '10px 8px 10px 0px',
  },
  posRankPercentileCell: {
    color: COLOR_DARK_GRAY,
    fontWeight: 600,
    height: '20px',
    paddingLeft: theme.spacing(0.5),
    textAlign: 'center',
    fontSize: theme.typography.pxToRem(12),
  },
  seasonLogHeadCell: {
    minWidth: '85px',
    padding: '10px',
    textAlign: 'center',

    '&:after': {
      content: '""',
      width: '1px',
      height: '12px',
      position: 'absolute',
      right: 0,
      background: COLOR_DARK_GRAY,
    }
  },
  seasonLogCell: {
    textAlign: 'center',
    padding: '10px 5px',
    verticalAlign: 'middle',
  },
  careerLogHeadCell: {
    minWidth: '85px',
    padding: '10px',
    textAlign: 'center',
    borderLeft: 0,
  },
  careerLogCell: {
    textAlign: 'center',
    padding: '10px 5px',
    verticalAlign: 'middle',
  },
  badgeWrap: {
    fontSize: theme.typography.pxToRem(14),
  },
  badgeVal: {
    fontWeight: 'bold',
    fontSize: theme.typography.pxToRem(12),
  },
  noValBadgeVal: {
    color: COLOR_DARK_GRAY,
    fontWeight: 400,
    fontSize: theme.typography.pxToRem(12),
    lineHeight: 1,
  },
  seasonalBlock: {
    display: 'grid',
    gridTemplateColumns: 'auto auto',
    justifyItems: 'start',
    
    '& .MuiTypography-root': {
      fontSize: theme.typography.pxToRem(14),
      display: 'contents',
    },
    '& b': {
      display: 'inline-block',
      textAlign: 'right',
      minWidth: '100%',
    },
    '& span': {
      marginLeft: 4,
    }
  },
  lowSpeedAndPower: {
    fontSize: theme.typography.pxToRem(16),
  },
  noScoreText: {
    color: COLOR_TEXT,
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 600,
  },
  tableHeadCellPadding: {
    color: COLOR_BLACK,
    paddingTop: theme.spacing(0.25),
    paddingBottom: theme.spacing(0.25),
    // paddingLef: theme.spacing(0),
    // paddingBottom: theme.spacing(0),
  },
  seasonLogTypography: {
    '& .MuiTypography-root': {
      fontSize: theme.typography.pxToRem(12),
      lineHeight: 1,
    },
  },
  noPaddingTableCell: {
    paddingTop: theme.spacing(0.25),
    paddingBottom: theme.spacing(0),
  },
  pdfScorePercentiles: {
    fontSize: theme.typography.pxToRem(14),
    height: '15px',
    lineHeight: 1.5,
  }
}), { name: DepthChart.name });

export default function DepthChart (props: DepthChartProps) {
  const {
    className,
    printed = false,
    depthChartPositions = [],
    selectedPlayers = [],
    setSelectedPlayers = () => {},
    editedPlayers = [],
    teamPlayers = [],
    fetchPlayerDetails,
    createDepthChartPlayerFromPlayer,
    setEditedPlayers = () => {},
    onUpdatePositions = () => {},
    onUpdatePlayers = () => {},
    onUpdateLabel = () => {},
  } = props;
  const classes = useStyles();

  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [loadingDepthChartPlayerDetailsId, setLoadingDepthChartPlayerDetailsId] = useState<number>(0);
  const [originalEditedPlayers, setOriginalEditedPlayers] = useState<DepthChartPlayer[]>([]);

  function onCheckBoxChange (checkedPlayer:DepthChartPlayer) {
    return (value:boolean) => {
      if (value) {
        setSelectedPlayers([...selectedPlayers, checkedPlayer]);
      } else {
        const selectedItemsWithoutCheckedPlayer = [...selectedPlayers];
        const removeIndex = selectedPlayers.findIndex(player => player.id === checkedPlayer.id);
        if (removeIndex > -1) {
          selectedItemsWithoutCheckedPlayer.splice(removeIndex, 1);
        }

        setSelectedPlayers(selectedItemsWithoutCheckedPlayer);
      }
    };
  }

  function onEdit (editedPlayer:DepthChartPlayer) {
    return () => {
      const positionWithEditedPlayer:any = depthChartPositions
        .find((position:DepthChartPosition) => position.players.some(player => player.id === editedPlayer.id));

      if (positionWithEditedPlayer && positionWithEditedPlayer.players && !!positionWithEditedPlayer.players.length) {
        setEditedPlayers([...editedPlayers, ...positionWithEditedPlayer.players]);
        setOriginalEditedPlayers([...editedPlayers, ...positionWithEditedPlayer.players]);
      }
    };
  }

  function onCancelEdit (editedPlayer:DepthChartPlayer) {
    return () => {
      const positionWithEditedPlayer:any = depthChartPositions
        .find((position:DepthChartPosition) => position.players.some(player => player.id === editedPlayer.id));

      if (!positionWithEditedPlayer || !positionWithEditedPlayer.players || !positionWithEditedPlayer.players.length) {
        return;
      }

      const playerId1 = get(positionWithEditedPlayer, 'players[0].id');
      const playerId2 = get(positionWithEditedPlayer, 'players[1].id');
      const originalPlayer1 = typeof playerId1 === 'number'
        ? originalEditedPlayers.find(player => player.id === playerId1)
        : undefined;
      const originalPlayer2 = typeof playerId2 === 'number'
        ? originalEditedPlayers.find(player => player.id === playerId2)
        : undefined;

      const originalEditedPlayersWithoutCancelledPlayers = [...originalEditedPlayers];

      if (originalPlayer1) {
        const originalRemoveIndex1 = originalEditedPlayers.findIndex(player => player.id === originalPlayer1.id);
        if (originalRemoveIndex1 > -1) {
          originalEditedPlayersWithoutCancelledPlayers.splice(originalRemoveIndex1, 1);
        }
      }

      if (originalPlayer2) {
        const originalRemoveIndex2 = originalEditedPlayersWithoutCancelledPlayers.findIndex(player => player.id === originalPlayer2.id);
        if (originalRemoveIndex2 > -1) {
          originalEditedPlayersWithoutCancelledPlayers.splice(originalRemoveIndex2, 1);
        }
      }

      onUpdatePlayers([originalPlayer1, originalPlayer2].filter(Boolean) as DepthChartPlayer[]);

      setOriginalEditedPlayers(originalEditedPlayersWithoutCancelledPlayers);
      stopEditing(editedPlayer);
    };
  }

  function onAcceptEdit (editedPlayer:DepthChartPlayer) {
    return () => stopEditing(editedPlayer);
  }

  function stopEditing (editedPlayer:DepthChartPlayer) {
    const positionWithEditedPlayer:any = depthChartPositions
      .find((position:DepthChartPosition) => position.players.some(player => player.id === editedPlayer.id));
    const originalPlayer1 = positionWithEditedPlayer.players[0];
    const originalPlayer2 = positionWithEditedPlayer.players[1];

    const editedPlayersWithoutCancelledPlayers = [...editedPlayers];

    const removeIndex1 = editedPlayers.findIndex(player => player.id === originalPlayer1.id);
    if (removeIndex1 > -1) {
      editedPlayersWithoutCancelledPlayers.splice(removeIndex1, 1);
    }
    const removeIndex2 = editedPlayersWithoutCancelledPlayers.findIndex(player => player.id === originalPlayer2.id);
    if (removeIndex2 > -1) {
      editedPlayersWithoutCancelledPlayers.splice(removeIndex2, 1);
    }

    setEditedPlayers(editedPlayersWithoutCancelledPlayers);
  }

  function onInputChange (fieldName:string, editedPlayer:DepthChartPlayer) {
    return (value:string) => {
      const updatedPlayer:any = {...editedPlayer};
      updatedPlayer[fieldName] = value;

      onUpdatePlayers([updatedPlayer as DepthChartPlayer]);
    };
  }

  function onLabelChange (depthChartPosition:DepthChartPosition) {
    return (value:string) => {
      onUpdateLabel(depthChartPosition, value);
    };
  }

  function onSelectPlayer (editedDepthChartPlayer:DepthChartPlayer) {
    return (selectedPlayer:Player) => {
      setLoadingDepthChartPlayerDetailsId(editedDepthChartPlayer.id);

      fetchPlayerDetails(selectedPlayer.id)
        .then(createDepthChartPlayerFromPlayer(editedDepthChartPlayer))
        .then((newDepthChartPlayer:DepthChartPlayer) => {
          if (newDepthChartPlayer) {
            onUpdatePlayers([newDepthChartPlayer]);
          }
        })
        .finally(() => setLoadingDepthChartPlayerDetailsId(0))
    };
  }

  function onUpdateCustomName (editedPlayer:DepthChartPlayer) {
    return (customName:string) => {
      const updatedPlayer:any = {...editedPlayer};
      updatedPlayer.playerName = customName;

      onUpdatePlayers([updatedPlayer as DepthChartPlayer]);
    };
  }

  function onDragStart () {
    setIsDragging(true);
  }

  function onDragEnd (result:any) {
    setIsDragging(false);

    if (!result.destination) return;

    const reorderedPositions = [...depthChartPositions] as any[];

    const fromIndex = result.source.index;
    const toIndex = result.destination.index;

    const fromPositionIndex = Math.floor(fromIndex / 2);
    const toPositionIndex = Math.floor(toIndex / 2);

    const fromPositionPlayerIndex = fromIndex % 2;
    const toPositionPlayerIndex = toIndex % 2;

    const fromPlayer = (reorderedPositions[fromPositionIndex] as DepthChartPosition).players[fromPositionPlayerIndex];
    const toPlayer = (reorderedPositions[toPositionIndex] as DepthChartPosition).players[toPositionPlayerIndex];

    (reorderedPositions[fromPositionIndex] as DepthChartPosition).players[fromPositionPlayerIndex] = {
      ...toPlayer,
      id: fromPlayer.id,
    } as DepthChartPlayer;
    (reorderedPositions[toPositionIndex] as DepthChartPosition).players[toPositionPlayerIndex] = {
      ...fromPlayer,
      id: toPlayer.id,
    } as DepthChartPlayer;

    onUpdatePositions(reorderedPositions);
  }

  function renderRow (index:number, depthChartPosition:DepthChartPosition, player:DepthChartPlayer) {
    const label = depthChartPosition.label;
    const checked = (!!player && !!player.id)
      && !!selectedPlayers.find((oneOfSelected:DepthChartPlayer) => oneOfSelected.id === player.id);
    const edited = (!!player && !!player.id) && !!editedPlayers.find((oneOfEdited:DepthChartPlayer) => oneOfEdited.id === player.id);
    const even = (index + 1) % 2 === 1;
    const draggableItemId = `${player && player.id ? player.id : ''}_${index}`;

    return (
      <Draggable
        key={index}
        draggableId={draggableItemId}
        index={index}
      >
        {({ draggableProps, dragHandleProps, innerRef }, snapshot) => (
          <TableRow
            className={clsx(
              classes.tableRow,
              edited && classes.editedTableRow,
              snapshot.isDragging && classes.tableRowDragging
            )}
            ref={innerRef}
            {...draggableProps}
            {...dragHandleProps}
          >
            {(even || isDragging) && (
              <TableCell
                key='position'
                className={clsx(classes.tableCell, classes.positionCell, edited && classes.edited, classes.posHeadCell, printed && classes.noPaddingTableCell)}
                rowSpan={isDragging ? 1 : 2}
              >
                {edited
                ? <Input
                    className={classes.input}
                    inputFieldClassName={clsx(classes.inputField, classes.positionInputField)}
                    fontSize={18}
                    value={label}
                    autoFocus
                    onChange={onLabelChange(depthChartPosition)}
                  />
                : label
                }
              </TableCell>
            )}

            <TableCell
              key='check-box-cell'
              className={clsx(
                classes.tableCell,
                classes.checkBoxCell,
                printed && classes.hidden,
                printed && classes.noPaddingTableCell
              )}
            >
              {!!player && (
                <Checkbox
                  checked={checked}
                  onChange={onCheckBoxChange(player)}
                />
              )}
            </TableCell>

            <TableCell
              className={clsx(
                classes.tableCell,
                classes.dndCell,
                printed && classes.hidden,
                printed && classes.noPaddingTableCell
              )}>
              <DragNDropDots className={classes.dragNDropDots} />
            </TableCell>

            {tableColumns.map((tableColumn:any) => (
              <TableCell
                key={tableColumn.value}
                className={clsx(
                  classes.tableCell,
                  tableColumn.className,
                  edited && classes.edited
                )}
                style={{
                  padding: tableColumn.value === "multiSport"
                    ? printed
                      ? '2px'
                      : '14px 0'
                    : '',
                  verticalAlign: !printed ? 'top' : undefined,

                }}
              >
                {tableColumn.renderContent(player)}
              </TableCell>
            ))}

            {((even || isDragging) && !printed) && (
              <TableCell
                key='edit-cell'
                className={clsx(classes.tableCell, classes.editCell, printed && classes.noPaddingTableCell)}
                rowSpan={isDragging ? 1 : 2}
              >
                <div className={classes.actions}>
                  {!edited && (
                    <Tooltip title='Edit Row'>
                      <EditButton
                        className={classes.editButton}
                        iconClassName={classes.editButtonIcon}
                        onClick={onEdit(player)}
                      />
                    </Tooltip>
                  )}
                  {edited && (
                    <>
                      <Tooltip title='Cancel'>
                        <CancelButton onClick={onCancelEdit(player)} />
                      </Tooltip>

                      <Tooltip title='Save'>
                        <DoneButton onClick={onAcceptEdit(player)} />
                      </Tooltip>
                    </>
                  )}
                </div>
              </TableCell>
            )}
          </TableRow>
        )}
      </Draggable>
    );
  }

  const tableColumns = DEFAULT_COLUMNS.map((column:string) => {
    switch (column) {
      case DEPTH_CHART_COLUMN.NUMBER:
        return {
          value: DEPTH_CHART_COLUMN.NUMBER,
          title: DEPTH_CHART_COLUMN_TITLE[DEPTH_CHART_COLUMN.NUMBER],
          headClassName: classes.jerseyNumberHeadCell,
          className: clsx(classes.jerseyNumberCell, printed && classes.noPaddingTableCell),
          renderContent: (player:DepthChartPlayer) => {
            const edited = player?.id && !!editedPlayers.find(oneOfEdited => oneOfEdited.id === player.id);

            return edited
              ? <Input
                  className={classes.input}
                  inputFieldClassName={classes.inputField}
                  fontSize={16}
                  value={player?.jerseyNumber || ''}
                  autoFocus
                  onChange={onInputChange('jerseyNumber', player)}
                />
              : player?.jerseyNumber || '';
          },
        };
      case DEPTH_CHART_COLUMN.NAME:
        return {
          value: DEPTH_CHART_COLUMN.NAME,
          title: DEPTH_CHART_COLUMN_TITLE[DEPTH_CHART_COLUMN.NAME],
          headClassName: classes.nameColumnHeadCell,
          className: clsx(classes.nameCell, printed && classes.noPaddingTableCell),
          renderContent: (player:DepthChartPlayer) => {
            const edited = player?.id && !!editedPlayers.find(oneOfEdited => oneOfEdited.id === player.id);
            const playerIsLoading = player?.id === loadingDepthChartPlayerDetailsId;

            if (player && player.id) {
              if (playerIsLoading) {
                return (
                  <Loader
                    className={classes.playerLoader}
                    spinnerClassName={classes.playerLoaderSpinner}
                    inProgress
                  />
                );
              }

              if (edited) {
                return (
                  <>
                  <div style={{display: 'flex', justifyContent: 'space-between'}}>
                    <div style={{width: '100%'}}>
                      <SelectPlayer
                        className={classes.selectPlayer}
                        players={teamPlayers}
                        selectedPlayer={player?.player || null}
                        onSelect={onSelectPlayer(player)}
                        customName={player?.playerName || ''}
                        onUpdateCustomName={onUpdateCustomName(player)}
                      />
                    </div>
                  </div>
                  </>
                );
              } else if (player?.player?.slug) {
                const name = player.playerName || (player?.player?.firstName
                  ? `${player.player.firstName} ${player.player?.lastName || ''}`
                  : '');

                return (
                  <div className={classes.playerNameWithLocationIcon}>
                    <Avatar
                      className={classes.playerAvatar}
                      src={player?.player.photoUrl}
                      alt='Profile picture'
                    />
                  <Link
                    className={classes.playerLink}
                    to={`/player/${player.player.slug}?`}
                    target='_blank'
                  >
                      <div className={classes.nameWrap}>
                        <span>
                          {player.duplicated && (
                            <ColorCode
                              className={classes.duplicatePlayer}
                              color={Color.YELLOW}
                              tooltipText='Duplicated Player'
                            />
                          )}
                          <Tooltip title={name}>
                            <span>{player?.player?.firstName}</span>
                          </Tooltip>
                        </span>
                        <Tooltip title={name}>
                          <span> {player?.player?.lastName}</span>
                        </Tooltip>
                      </div>
                  </Link>
                  </div>
                );
              }

              return null;
            }
          },
        };
      case DEPTH_CHART_COLUMN.HS_SPORTS:
        return {
          value: DEPTH_CHART_COLUMN.HS_SPORTS,
          title: DEPTH_CHART_COLUMN_TITLE[DEPTH_CHART_COLUMN.HS_SPORTS],
          headClassName: classes.multiSportHeadCell,
          className: clsx(classes.multiSportCell, printed && classes.noPaddingTableCell),
          renderContent: (player:DepthChartPlayer) => {
              const playerDepth = player?.player;
              let playerMulti = (playerDepth?.multiSport) || [];
              if (playerMulti.length > 3) {
                let splicedSport = JSON.stringify(playerMulti);
                const splicedSportFinal = JSON.parse(splicedSport).splice(0, 3);
                playerMulti = splicedSportFinal;
              }
              return playerMulti?.length > 0 ?
              (
                <Score
                  className={clsx(classes.playerScore, classes.multiSportScoreCell, printed && classes.playerScoreForPrint)}
                  // checkIconClass={classes.checkIconClass}
                  type={SCORE_TYPE.MULTISPORTS}
                  value={playerMulti?.length}
                  totalValue={playerMulti?.length}
                  multiSportTooltip={ <MultiSportList
                    iconClassName={classes.multiSportIcon}
                    list={playerMulti}
                  />
                  }
                />
              )
              : <MultiSportList
                  iconClassName={classes.multiSportIcon}
                  list={playerMulti}
                />
          },
        };
      case DEPTH_CHART_COLUMN.HEIGHT:
        return {
          value: DEPTH_CHART_COLUMN.HEIGHT,
          title: DEPTH_CHART_COLUMN_TITLE[DEPTH_CHART_COLUMN.HEIGHT],
          headClassName: classes.heightHeadCell,
          className: clsx(classes.heightCell, printed && classes.noPaddingTableCell),
          renderContent: (player:DepthChartPlayer) => {
            const edited = (player && player.id)
              && !!editedPlayers.find(oneOfEdited => oneOfEdited.id === player.id);

            return edited
              ? (
                <Input
                  className={classes.input}
                  inputFieldClassName={classes.inputField}
                  fontSize={16}
                  value={(player && player.height) ? String(player.height) : ''}
                  autoFocus
                  onChange={onInputChange('height', player)}
                />
              )
              : player?.height || '';
          },
        };
      case DEPTH_CHART_COLUMN.WEIGHT:
        return {
          value: DEPTH_CHART_COLUMN.WEIGHT,
          title: DEPTH_CHART_COLUMN_TITLE[DEPTH_CHART_COLUMN.WEIGHT],
          headClassName: classes.weightHeadCell,
          className: clsx(classes.weightCell, printed && classes.noPaddingTableCell),
          renderContent: (player:DepthChartPlayer) => {
            const edited = player?.id
              && !!editedPlayers.find(oneOfEdited => oneOfEdited.id === player.id);

            return edited
              ? (
                <Input
                  className={classes.input}
                  inputFieldClassName={classes.inputField}
                  fontSize={16}
                  value={player?.weight ? String(player.weight) : ''}
                  autoFocus
                  onChange={onInputChange('weight', player)}
                />
              )
              : player?.weight || ''
          },
        };
      case DEPTH_CHART_COLUMN.HS_SPEED_PERCENTILE:
        return {
          value: DEPTH_CHART_COLUMN.HS_SPEED_PERCENTILE,
          title: DEPTH_CHART_COLUMN_TITLE[DEPTH_CHART_COLUMN.HS_SPEED_PERCENTILE],
          headClassName: classes.hsSpeedAndPowerHeadCell,
          className: clsx(classes.hsSpeedCell, printed && classes.noPaddingTableCell),
          renderContent: (player:DepthChartPlayer) => {
            const rawPercentile = player?.hsSpeedPercentile;
            const speed = Number(rawPercentile);
            const showSpeed = rawPercentile != null && !isNaN(speed);
            return (
              <div style={{ fontSize: (speed > 75 && player?.player?.isSpeedRecruit) ? '' : '1rem', display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                {showSpeed && (
                  <>
                    <span className={clsx(classes.lowSpeedAndPower)}>{Math.round(speed)}%</span>
                    {speed > 75 && player?.player?.isSpeedRecruit && (
                      <ColorCode
                        className={classes.speedAndPowerRecuitDot}
                        color={Color.ORANGE}
                        tooltipText='Speed Player'
                      />
                    )}
                  </>
                )}
              </div>
            );
          },
        }
      case DEPTH_CHART_COLUMN.HS_POWER_PERCENTILE:
        return {
          value: DEPTH_CHART_COLUMN.HS_POWER_PERCENTILE,
          title: DEPTH_CHART_COLUMN_TITLE[DEPTH_CHART_COLUMN.HS_POWER_PERCENTILE],
          headClassName: classes.hsSpeedAndPowerHeadCell,
          className: clsx(classes.hsPowerCell, printed && classes.noPaddingTableCell),
          renderContent: (player:DepthChartPlayer) => {
            const rawPercentile = player?.hsPowerPercentile;
            const power = Number(rawPercentile);
            const showPower = rawPercentile != null && !isNaN(power);
            return (
              <div>
              <div style={{ fontSize: (power > 75 && player?.player?.isPowerRecruit) ? '' : '1rem', display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                {showPower && (
                  <>
                    <span className={clsx(classes.lowSpeedAndPower)}>{Math.round(power)}%</span>
                    {power > 75 && player?.player?.isPowerRecruit && (
                      <ColorCode
                        className={classes.speedAndPowerRecuitDot}
                        color={Color.GREEN}
                        tooltipText='Power Player'
                      />
                    )}
                  </>
                )}
              </div>
              </div>
            );
          },
        }
      case DEPTH_CHART_COLUMN.CLASS:
        return {
          value: DEPTH_CHART_COLUMN.CLASS,
          title: DEPTH_CHART_COLUMN_TITLE[DEPTH_CHART_COLUMN.CLASS],
          headClassName: classes.classHeadCell,
          className: clsx(classes.classCell, printed && classes.noPaddingTableCell),
          renderContent: (player:DepthChartPlayer) => {
            const edited = player?.id
              && !!editedPlayers.find(oneOfEdited => oneOfEdited.id === player.id);

            return edited
              ? (
                <Input
                  className={classes.input}
                  inputFieldClassName={clsx(classes.inputField, classes.classInputField)}
                  fontSize={16}
                  value={player?.class ? String(player.class) : ''}
                  autoFocus
                  onChange={onInputChange('class', player)}
                />
              )
              : player?.class || ''
          },
        };
      case DEPTH_CHART_COLUMN.PAI_SCORE:
        return {
          value: DEPTH_CHART_COLUMN.PAI_SCORE,
          title: DEPTH_CHART_COLUMN_TITLE[DEPTH_CHART_COLUMN.PAI_SCORE],
          headClassName: classes.playerScoreHeadCell,
          className: clsx(classes.playerScoreCell, printed && classes.noPaddingTableCell),
          renderContent: (player:DepthChartPlayer) => {
            if (!player || !player.player) return null;

            const pai = (player?.player?.pai) || [];
            const paiDiv1Percentile = player?.player?.pai?.find(item => item.isPrimary)?.paiDiv1Percentile || null;
            let result = '';
            if (!!player?.player) {
              result = paiDiv1Percentile ? `${Math.round(Number(paiDiv1Percentile))}%` : player.paiPercentile ? `${player.paiPercentile}%` : ''
            }
            return (
              <>
                <Score
                  className={clsx(classes.playerScore, printed && classes.playerScoreForPrint)}
                  type={SCORE_TYPE.PAI}
                  scoreList={pai}
                />
                <div className={clsx(classes.paiPercentileCell, printed && classes.pdfScorePercentiles)}>
                  <span>{result}</span>
                </div>
              </>
            );
          },
        };
      case DEPTH_CHART_COLUMN.PPI_SCORE:
      return {
        value: DEPTH_CHART_COLUMN.PPI_SCORE,
        title: DEPTH_CHART_COLUMN_TITLE[DEPTH_CHART_COLUMN.PPI_SCORE],
        headClassName: classes.playerScoreHeadCell,
        className: clsx(classes.playerScoreCell, printed && classes.noPaddingTableCell),
        renderContent: (player:DepthChartPlayer) => {
          if (!player || !player.player) return null;

          const ppi = (player?.player?.ppi);
          const ppiDiv1Percentile = !!player?.player ? player?.player?.ppiDiv1Percentile : null;
          let result = '';
          if (!!player?.player) {
            result = ppiDiv1Percentile ? `${Math.round(Number(ppiDiv1Percentile))}%` : player.ppiPercentile ? `${player.ppiPercentile}%` : ''
          }
          return (
            <>
              <Score
                className={clsx(classes.playerScore, printed && classes.playerScoreForPrint)}
                type={SCORE_TYPE.PPI}
                value={ppi || undefined}
              />
              <div className={clsx(classes.ppiPercentileCell, printed && classes.pdfScorePercentiles)}>
                <span>{result}</span>
              </div>
            </>
          );
        },
      };
      case DEPTH_CHART_COLUMN.PPI_PERCENTILE:
        return {
          value: DEPTH_CHART_COLUMN.PPI_PERCENTILE,
          title: DEPTH_CHART_COLUMN_TITLE[DEPTH_CHART_COLUMN.PPI_PERCENTILE],
          headClassName: classes.ppiPercentileHeadCell,
          className: clsx(classes.ppiPercentileCell, printed && classes.noPaddingTableCell),
          renderContent: (player:DepthChartPlayer) => {
            return !!player?.player
              ? player?.player?.ppiDiv1Percentile ? `${Math.round(Number(player?.player?.ppiDiv1Percentile))}%` : (player.ppiPercentile ? `${player.ppiPercentile}%` : '')
              : null;
          },
        };
      case DEPTH_CHART_COLUMN.COMBINE_SCORE:
        return {
          value: DEPTH_CHART_COLUMN.COMBINE_SCORE,
          title: DEPTH_CHART_COLUMN_TITLE[DEPTH_CHART_COLUMN.COMBINE_SCORE],
          headClassName: classes.playerScoreHeadCell,
          className: clsx(classes.playerScoreCell, printed && classes.noPaddingTableCell),
          renderContent: (player:DepthChartPlayer) => {
            if (!player || !player.player) return null;

            const combine = player?.player?.combine || 0;
            const hsCombines = player?.player?.hsCombines || [];
            const combineDiv1Percentile = player?.player?.combine?.find(item => item.isPrimary)?.combineDiv1Percentile || null;
            let result = '';
            if (!!player?.player) {
              result = combineDiv1Percentile ? `${Math.round(Number(combineDiv1Percentile))}%` : player.combinePercentile ? `${player.combinePercentile}%` : ''
            }
            return (
              <>
                <Score
                  className={clsx(classes.playerScore, printed && classes.playerScoreForPrint)}
                  type={SCORE_TYPE.COMBINE}
                  scoreList={combine}
                  combines={hsCombines}
                />
                <div className={clsx(classes.combinePercentileCell, printed && classes.pdfScorePercentiles)}>
                  <span>{result}</span>
                </div>
              </>
            );
          },
        };
      case DEPTH_CHART_COLUMN.POS_RANK:
        return {
          value: DEPTH_CHART_COLUMN.POS_RANK,
          title: DEPTH_CHART_COLUMN_TITLE[DEPTH_CHART_COLUMN.POS_RANK],
          headClassName: classes.tfPosRankHeadCell,
          className: clsx(classes.tfPosRankCell, printed && classes.noPaddingTableCell),
          renderContent: (player:DepthChartPlayer) => {
            // if (!player || !player?.tfPosRank) return null;
            let posRank: string | number = '';
            if (player?.tfPosRank && player?.tfPosRank > 999) {
              posRank = '1k+';
            } else {
              posRank = player?.tfPosRank ?? ""
            }
            const div1 = !!player?.tfPosPerc ? `${Math.round(Number(player?.tfPosPerc))}%` : '';
            
            if (!player?.player) return null;

            return (
              <>
                {!player || !player?.tfPosRank ?
                  <CircleBadge
                    className={{
                      badgeWrap: classes.badgeWrap,
                    }}
                    color={COLOR_MIDDLE_GRAY}
                    value={<NotAvailable label={'N/R'} className={classes.noScoreText} /> }
                    height={32} 
                    width={32}  
                  />
                : <CircleBadge
                  className={classes}
                  value={posRank}
                  height={32} 
                  width={32}
                />
                }
                <div className={clsx(classes.posRankPercentileCell, printed && classes.pdfScorePercentiles)}>
                  <span>{div1}</span>
                </div>
              </>
            );
          },
        }
      case DEPTH_CHART_COLUMN.SEASON_LOG:
          return {
          value: DEPTH_CHART_COLUMN.SEASON_LOG,
          title: DEPTH_CHART_COLUMN_TITLE[DEPTH_CHART_COLUMN.SEASON_LOG],
          headClassName: classes.seasonLogHeadCell,
          className: clsx(classes.seasonLogCell, printed && classes.seasonLogTypography, printed && classes.noPaddingTableCell),
          renderContent: (player:DepthChartPlayer) => {
            const startsVal = player?.startsSeason ?? 0;
            const gamesVal = player?.gamesSeason ?? 0;
            const snapsVal = player?.snapsSeason ?? 0;
            if (!player?.player) return null;

            return <div className={classes.seasonalBlock}>
              <Typography><b>{startsVal}</b><span>Starts</span></Typography>
              <Typography><b>{gamesVal}</b><span>Games</span></Typography>
              <Typography><b>{snapsVal}</b><span>Snaps</span></Typography>
            </div>
          }
        }
      case DEPTH_CHART_COLUMN.CAREER_LOG:
        return {
          value: DEPTH_CHART_COLUMN.CAREER_LOG,
          title: DEPTH_CHART_COLUMN_TITLE[DEPTH_CHART_COLUMN.CAREER_LOG],
          headClassName: classes.careerLogHeadCell,
          className: clsx(classes.careerLogCell, printed && classes.seasonLogTypography, printed && classes.noPaddingTableCell),
          renderContent: (player:DepthChartPlayer) => {
            const startsVal = player?.startsCareer ?? 0;
            const gamesVal = player?.gamesCareer ?? 0;
            const snapsVal = player?.snapsCareer ?? 0;
            
            if (!player?.player) return null;

            return <div className={classes.seasonalBlock}>
              <Typography><b>{startsVal}</b><span>Starts</span></Typography>
              <Typography><b>{gamesVal}</b><span>Games</span></Typography>
              <Typography><b>{snapsVal}</b><span>Snaps</span></Typography>
            </div>
          }
        }
      default:
        return null;
    }
  });

  return (
    <DragDropContext
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      <Droppable droppableId='depth-chart'>
        {({ droppableProps, placeholder, innerRef }) => (
          <div className={classes.depthChartWrap}>
            <Table
              className={clsx(
                classes.depthChart,
                isDragging && classes.isDragging,
                className,
              )}
              ref={innerRef}
              {...droppableProps}
            >
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableHeadCell 
                    className={clsx(
                      classes.posHeadCell,
                      printed && classes.tableHeadCellPadding
                    )} 
                    key='position'
                  >
                    Pos.
                  </TableHeadCell>

                  <TableHeadCell
                    key='check-box-cell'
                    className={clsx(
                      classes.checkBoxCell,
                      printed && classes.hidden,
                      printed && classes.tableHeadCellPadding
                    )}
                  >
                    <Checkbox
                      checked={selectedPlayers.length === (depthChartPositions.length * 2)}
                      onChange={() => (selectedPlayers.length === (depthChartPositions.length * 2))
                        ? setSelectedPlayers([])
                        : setSelectedPlayers(depthChartPositions
                          .map(position => position && position.players)
                          .flat() as DepthChartPlayer[]
                        )
                      }
                    />
                  </TableHeadCell>

                  <TableHeadCell
                    key='dnd-dots'
                    className={clsx(
                      classes.dndHeadCell,
                      printed && classes.hidden,
                      printed && classes.tableHeadCellPadding
                    )}
                  >&nbsp;</TableHeadCell>

                  {tableColumns.map((tableColumn:any) => (
                    <TableHeadCell
                    key={tableColumn.value}
                    className={clsx(
                      tableColumn.headClassName, 
                      tableColumn && tableColumn.value && tableColumn.value === DEPTH_CHART_COLUMN.NAME && classes.nameHeadCell,
                      printed && classes.tableHeadCellPadding
                    )}
                    >
                      {tableColumn.title}
                    </TableHeadCell>
                  ))}

                  <TableHeadCell
                    key='edit-cell'
                    className={clsx(
                      classes.editHeadCell,
                      printed && classes.hidden,
                      printed && classes.tableHeadCellPadding
                    )}
                  >{''}</TableHeadCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {depthChartPositions
                  .map((depthChartPosition:DepthChartPosition, index:number) => [
                    renderRow(index + index, depthChartPosition, depthChartPosition.players[0]),
                    renderRow(index + index + 1, depthChartPosition, depthChartPosition.players[1]),
                  ])
                  .flat()
                }
              </TableBody>
            </Table>
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
}
